import storage from "redux-persist/lib/storage";
import {
  Action,
  ThunkAction,
  configureStore,
  combineReducers,
} from "@reduxjs/toolkit";

import {
  FLUSH,
  PAUSE,
  PURGE,
  PERSIST,
  REGISTER,
  REHYDRATE,
  persistStore,
  persistReducer,
} from "redux-persist";

import type { BGStateType } from "./slices/bgSlice";
import type { TabStateType } from "./slices/tabSlice";
import type { CartStateType } from "./slices/cartDemoSlice";

import type { SortStateType } from "./slices/sortSlice";
import type { FilterStateType } from "./slices/filterSlice";
import type { WishlistStateType } from "./slices/wishlistSlice";
import type { VerificationStateType } from "./slices/verificationSlice";
import type { ScreenStateType } from "./slices/screenSlice";
import type { AuthStateType } from "./slices/authSlice";
import type { SelectedSiteState } from "./slices/selectedSiteSlice";
import type { CartState } from "./slices/cartSlice";
import type { AppReducerState } from "./slices/appSlice";

import { bgSlice } from "./slices/bgSlice";
import { tabSlice } from "./slices/tabSlice";
import { userSlice } from "./slices/userSlice";
import { cartDemoSlice } from "./slices/cartDemoSlice";
import { sortSlice } from "./slices/sortSlice";
import { filterSlice } from "./slices/filterSlice";
import { paymentSlice } from "./slices/paymentSlice";
import { wishlistSlice } from "./slices/wishlistSlice";
import { promocodeSlice } from "./slices/promocodeSlice";
import { firstLaunchSlice } from "./slices/firstLaunchSlice";

import { screenReducer } from "./slices/screenSlice";
import { authReducer } from "./slices/authSlice";
import { appReducer } from "./slices/appSlice";
import { cartReducer } from "./slices/cartSlice";
import { selectedSiteReducer } from "./slices/selectedSiteSlice";
import { lookupReducer, LookupState } from "./slices/lookupSlice";


const rootReducer = combineReducers({
  bgSlice: bgSlice.reducer,
  tabSlice: tabSlice.reducer,
  userSlice: userSlice.reducer,
  cartDemoSlice: cartDemoSlice.reducer,
  sortSlice: sortSlice.reducer,
  filterSlice: filterSlice.reducer,
  paymentSlice: paymentSlice.reducer,
  wishlistSlice: wishlistSlice.reducer,
  promocodeSlice: promocodeSlice.reducer,
  firstLaunchSlice: firstLaunchSlice.reducer,
  screenReducer: screenReducer.reducer,
  authReducer: authReducer.reducer,
  appReducer: appReducer.reducer,
  selectedSiteReducer: selectedSiteReducer.reducer,
  cartReducer: cartReducer.reducer,
  lookupReducer: lookupReducer.reducer
});

const persistConfig = {
  key: "root",
  storage,
  whitelist: [
    "selectedSiteReducer",
    "appReducer",
    "screenReducer",
    "authReducer",
    "tabSlice",
    "sortingSlice",
    "cartSlice",
    "cartReducer",
    "wishlistSlice",
  ],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(),
});

export interface RootState {
  bgSlice: BGStateType;
  cartDemoSlice: CartStateType;
  sortSlice: SortStateType;
  tabSlice: TabStateType;
  filterSlice: FilterStateType;
  wishlistSlice: WishlistStateType;
  verificationSlice: VerificationStateType;
  screenReducer: ScreenStateType;
  authReducer: AuthStateType;
  appReducer: AppReducerState;
  selectedSiteReducer: SelectedSiteState;
  cartReducer: CartState;
  lookupReducer: LookupState;
}

export const persistor = persistStore(store);

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
